import {
	AlertCircle,
	FileText,
	ImageIcon,
	Loader2,
	Upload,
	X,
} from 'lucide-react'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { trpc } from '@/trpc/client'

const ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf'
const ALLOWED_MIMES = new Set([
	'image/jpeg',
	'image/png',
	'image/webp',
	'application/pdf',
])
const MAX_BYTES = 10 * 1024 * 1024

export interface UploadedFile {
	fileKey: string
	fileMimeType: 'image/jpeg' | 'image/png' | 'image/webp' | 'application/pdf'
	fileSizeBytes: number
	fileOriginalName: string
}

interface FileUploadProps {
	value: UploadedFile | null
	onChange: (file: UploadedFile | null) => void
	// Fired as soon as we have a fileKey, BEFORE the S3 PUT completes, so the
	// caller can start a parallel OCR request against the same File. The S3
	// upload and OCR then race independently.
	onSelected?: (file: File, fileKey: string) => void
	disabled?: boolean
}

type UploadState =
	| { phase: 'idle' }
	| { phase: 'uploading'; progress: number; fileName: string }
	| { phase: 'error'; message: string }

function formatSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function uploadWithProgress(
	url: string,
	file: File,
	onProgress: (pct: number) => void,
): Promise<void> {
	return new Promise((resolve, reject) => {
		const xhr = new XMLHttpRequest()
		xhr.open('PUT', url)
		xhr.setRequestHeader('Content-Type', file.type)
		xhr.upload.addEventListener('progress', (e) => {
			if (e.lengthComputable) {
				onProgress(Math.round((e.loaded / e.total) * 100))
			}
		})
		xhr.addEventListener('load', () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				resolve()
			} else {
				reject(new Error(`Upload failed (${xhr.status})`))
			}
		})
		xhr.addEventListener('error', () =>
			reject(new Error('Network error during upload')),
		)
		xhr.addEventListener('abort', () => reject(new Error('Upload aborted')))
		xhr.send(file)
	})
}

export function FileUpload({
	value,
	onChange,
	onSelected,
	disabled,
}: FileUploadProps) {
	const [state, setState] = useState<UploadState>({ phase: 'idle' })
	const inputRef = useRef<HTMLInputElement | null>(null)
	const createUploadUrl = trpc.files.createUploadUrl.useMutation()

	async function handleFile(file: File) {
		if (!ALLOWED_MIMES.has(file.type)) {
			setState({
				phase: 'error',
				message: 'Only JPG, PNG, WebP, and PDF files are allowed.',
			})
			return
		}
		if (file.size > MAX_BYTES) {
			setState({
				phase: 'error',
				message: `File is too large (max ${formatSize(MAX_BYTES)}).`,
			})
			return
		}

		setState({ phase: 'uploading', progress: 0, fileName: file.name })

		try {
			const { url, key } = await createUploadUrl.mutateAsync({
				mimeType: file.type as UploadedFile['fileMimeType'],
				sizeBytes: file.size,
				originalFileName: file.name,
			})

			// Fire onSelected first so OCR can start in parallel with the S3 PUT.
			onSelected?.(file, key)

			await uploadWithProgress(url, file, (pct) =>
				setState({ phase: 'uploading', progress: pct, fileName: file.name }),
			)

			onChange({
				fileKey: key,
				fileMimeType: file.type as UploadedFile['fileMimeType'],
				fileSizeBytes: file.size,
				fileOriginalName: file.name,
			})
			setState({ phase: 'idle' })
			toast.success('File uploaded')
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Upload failed'
			setState({ phase: 'error', message })
		}
	}

	function clear() {
		onChange(null)
		setState({ phase: 'idle' })
		if (inputRef.current) inputRef.current.value = ''
	}

	if (state.phase === 'uploading') {
		return (
			<div className="flex items-center gap-3 rounded-lg border bg-card p-4">
				<Loader2 className="size-5 shrink-0 animate-spin text-primary" />
				<div className="flex-1 min-w-0">
					<p className="truncate text-sm font-medium">{state.fileName}</p>
					<div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
						<div
							className="h-full bg-primary transition-[width]"
							style={{ width: `${state.progress}%` }}
						/>
					</div>
					<p className="mt-1 text-xs tabular-nums text-muted-foreground">
						{state.progress}% uploaded
					</p>
				</div>
			</div>
		)
	}

	if (value) {
		const Icon = value.fileMimeType === 'application/pdf' ? FileText : ImageIcon
		return (
			<div className="flex items-center gap-3 rounded-lg border bg-card p-4">
				<div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
					<Icon className="size-5" />
				</div>
				<div className="flex-1 min-w-0">
					<p className="truncate text-sm font-medium">
						{value.fileOriginalName}
					</p>
					<p className="text-xs tabular-nums text-muted-foreground">
						{formatSize(value.fileSizeBytes)}
					</p>
				</div>
				<Button
					type="button"
					variant="ghost"
					size="icon"
					onClick={clear}
					disabled={disabled}
					aria-label="Remove file"
				>
					<X className="size-4" />
				</Button>
			</div>
		)
	}

	return (
		<div className="space-y-2">
			<button
				type="button"
				onClick={() => inputRef.current?.click()}
				disabled={disabled}
				className={cn(
					'flex w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed bg-card/50 px-6 py-8 text-center transition-colors',
					'hover:border-primary/40 hover:bg-accent',
					'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
					'disabled:opacity-50 disabled:cursor-not-allowed',
				)}
			>
				<Upload className="size-5 text-muted-foreground" />
				<div className="space-y-0.5">
					<p className="text-sm font-medium">Click to upload a receipt</p>
					<p className="text-xs text-muted-foreground">
						JPG, PNG, WebP, or PDF — up to {formatSize(MAX_BYTES)}
					</p>
				</div>
			</button>
			<input
				ref={inputRef}
				type="file"
				accept={ACCEPT}
				className="hidden"
				disabled={disabled}
				onChange={(e) => {
					const file = e.target.files?.[0]
					if (file) handleFile(file)
				}}
			/>
			{state.phase === 'error' ? (
				<div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
					<AlertCircle className="mt-0.5 size-4 shrink-0" />
					<div className="flex-1">
						<p className="font-medium">Upload failed</p>
						<p className="text-destructive/90">{state.message}</p>
					</div>
					<Button
						type="button"
						variant="ghost"
						size="sm"
						onClick={() => setState({ phase: 'idle' })}
					>
						Retry
					</Button>
				</div>
			) : null}
		</div>
	)
}
